import { createContext, ReactNode, useContext, useState } from 'react';
import { LanguageType } from 'types/enums';

interface SurveyContextValue {
	language: LanguageType;
	setLanguage: (language: LanguageType) => void;
	surveyId: string;
	pinCode: string | null;
	setPinCode: (pinCode: string | null) => void;
	clearPinCode: () => void;
}

const SurveyContext = createContext<SurveyContextValue | undefined>(undefined);

interface SurveyProviderProps {
	children: ReactNode;
	surveyId: string;
	initialLanguage?: LanguageType;
}

const getBrowserLanguage = (): LanguageType => {
	const browserLang = navigator.language || navigator.languages?.[0] || 'en';
	const languageCode = browserLang.toLowerCase().split('-')[0];

	switch (languageCode) {
		case 'nb':
		case 'no':
		case 'nn':
			return LanguageType.Norwegian;
		case 'en':
			return LanguageType.English;
		default:
			return LanguageType.English;
	}
};

export const SurveyProvider = ({ children, surveyId, initialLanguage }: SurveyProviderProps) => {
	const [language, setLanguage] = useState<LanguageType>(initialLanguage || getBrowserLanguage());
	const [pinCode, setPinCode] = useState<string | null>(null);

	const clearPinCode = () => setPinCode(null);

	return (
		<SurveyContext.Provider
			value={{
				language,
				setLanguage,
				surveyId,
				pinCode,
				setPinCode,
				clearPinCode
			}}
		>
			{children}
		</SurveyContext.Provider>
	);
};

export const useSurveyContext = () => {
	const context = useContext(SurveyContext);
	if (context === undefined) {
		throw new Error('useSurveyContext must be used within a SurveyProvider');
	}
	return context;
};
